// src/autodesk_usage/components/customerDetail/ProductLLMPanel.tsx
// All comments in English only

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Collapsible,
    CollapsibleTrigger,
    CollapsibleContent,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";

interface CrmRecommendation {
    action?: string;
    description?: string;
    owner?: string;
    timing_hint?: string;
    confidence?: string;
    playbook_tag?: string;
    kpis?: string[];
    expected_impact?: string;
}

interface ProductLLMPanelProps {
    product: string;

    // High-level texts
    analysis?: string | null;
    actionsText?: string | null;

    // Structured reasoning
    riskSummary?: Record<string, boolean | null> | null;
    explainCodes?:
    | string[]
    | {
        [key: string]: string | null;
    }
    | null;

    // Model metadata
    llmModelName?: string | null;
    llmGeneratedAt?: string | null;
    dataVersion?: string | null;

    // Debug / raw fields
    llmPromptRaw?: string | null;
    llmRawOutput?: unknown;
}

const RISK_LABELS: Record<string, string> = {
    overuse: "Overuse signal",
    usage_up: "Usage up",
    usage_down: "Usage down",
    license_up: "License up",
    license_down: "License down",
    trend_down: "Trend down",
    low_utilization: "Low utilization",
};

export default function ProductLLMPanel({
    product,
    analysis,
    actionsText,
    riskSummary,
    explainCodes,
    llmModelName,
    llmGeneratedAt,
    dataVersion,
    llmPromptRaw,
    llmRawOutput,
}: ProductLLMPanelProps) {
    // Derive overall_comment from llmRawOutput if present
    let overallComment: string | undefined;
    let crmRecommendations: CrmRecommendation[] = [];

    if (llmRawOutput && typeof llmRawOutput === "object") {
        const raw = llmRawOutput as {
            overall_comment?: unknown;
            crm_recommendation?: unknown;
            [key: string]: unknown;
        };

        if (typeof raw.overall_comment === "string") {
            overallComment = raw.overall_comment;
        }

        if (Array.isArray(raw.crm_recommendation)) {
            crmRecommendations = raw.crm_recommendation as CrmRecommendation[];
        }
    }

    // Fallback parsing of actionsText (JSON string) if crm_recommendation not available
    if (crmRecommendations.length === 0 && actionsText) {
        try {
            const parsed = JSON.parse(actionsText);
            if (Array.isArray(parsed)) {
                crmRecommendations = parsed as CrmRecommendation[];
            } else if (
                parsed &&
                typeof parsed === "object" &&
                Array.isArray((parsed as any).crm_recommendation)
            ) {
                crmRecommendations = (parsed as any)
                    .crm_recommendation as CrmRecommendation[];
            }
        } catch {
            // ignore parse errors, keep empty array
        }
    }

    // Choose main summary text with proper fallback
    const summaryText =
        overallComment ??
        analysis ??
        "No LLM analysis available for this product.";

    // Normalize risk signals: only keep flags that are true
    const riskEntries =
        riskSummary &&
        Object.entries(riskSummary).filter(([_, v]) => v === true);

    // Normalize explain codes into a flat string array
    const explainList: string[] = [];
    if (Array.isArray(explainCodes)) {
        explainCodes.forEach((code) => {
            if (code) explainList.push(code);
        });
    } else if (explainCodes && typeof explainCodes === "object") {
        Object.entries(explainCodes).forEach(([key, value]) => {
            if (value) {
                explainList.push(value);
            } else {
                explainList.push(key);
            }
        });
    }

    return (
        <Card className="w-full">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">
                    {product} — LLM Analysis
                </CardTitle>

                <div className="flex flex-col items-end gap-1 text-[10px] text-muted-foreground">
                    {dataVersion && <span>Data: {dataVersion}</span>}
                    {(llmModelName || llmGeneratedAt) && (
                        <span>
                            {llmModelName && <>Model: {llmModelName}</>}
                            {llmGeneratedAt && (
                                <>
                                    {" "}
                                    • {llmGeneratedAt}
                                </>
                            )}
                        </span>
                    )}
                </div>
            </CardHeader>

            <CardContent className="space-y-4">
                {/* Summary section */}
                <section className="space-y-1">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Summary
                    </div>
                    <p className="text-sm leading-6 whitespace-pre-line">{summaryText}</p>
                </section>

                {/* Recommended actions section */}
                {crmRecommendations.length > 0 && (
                    <section className="space-y-2">
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Recommended actions
                        </div>
                        <div className="space-y-2">
                            {crmRecommendations.map((a, idx) => (
                                <div
                                    key={idx}
                                    className="border rounded-lg p-3 bg-muted/40 space-y-2"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-sm font-medium">
                                            {a.action || "Action"}
                                        </div>
                                        <div className="flex flex-wrap gap-1 text-[10px]">
                                            {a.owner && (
                                                <span className="rounded-full border px-2 py-0.5">
                                                    {a.owner}
                                                </span>
                                            )}
                                            {a.timing_hint && (
                                                <span className="rounded-full border px-2 py-0.5">
                                                    {a.timing_hint}
                                                </span>
                                            )}
                                            {a.playbook_tag && (
                                                <span className="rounded-full border px-2 py-0.5">
                                                    {a.playbook_tag}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {a.description && (
                                        <p className="text-xs leading-5 text-muted-foreground whitespace-pre-line">
                                            {a.description}
                                        </p>
                                    )}

                                    <div className="flex flex-wrap justify-between items-center gap-2 text-[11px]">
                                        {a.kpis && a.kpis.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {a.kpis.map((kpi, i) => (
                                                    <span
                                                        key={i}
                                                        className="rounded-full border px-2 py-0.5 bg-background"
                                                    >
                                                        {kpi}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {a.expected_impact && (
                                            <span className="text-muted-foreground">
                                                Impact: {a.expected_impact}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Risk signals & explain codes section */}
                {(riskEntries && riskEntries.length > 0) || explainList.length > 0 ? (
                    <section className="space-y-3">
                        {riskEntries && riskEntries.length > 0 && (
                            <div className="space-y-1">
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Risk signals
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {riskEntries.map(([key]) => (
                                        <span
                                            key={key}
                                            className="text-[11px] rounded-full border px-2 py-0.5 bg-muted"
                                        >
                                            {RISK_LABELS[key] ?? key}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {explainList.length > 0 && (
                            <div className="space-y-1">
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Explain codes
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {explainList.map((code) => (
                                        <span
                                            key={code}
                                            className="text-[11px] rounded-full border px-2 py-0.5 bg-muted"
                                        >
                                            {code}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                ) : null}

                {/* Debug section */}
                {(llmPromptRaw || llmRawOutput) && (
                    <section>
                        <Collapsible>
                            <CollapsibleTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-[11px] text-muted-foreground"
                                >
                                    View prompt &amp; raw JSON
                                </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-2 space-y-3">
                                {llmPromptRaw && (
                                    <div className="space-y-1">
                                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                            Prompt
                                        </div>
                                        <pre className="max-h-64 overflow-auto rounded-md bg-muted p-2 text-[11px] leading-4 whitespace-pre-wrap">
                                            {llmPromptRaw}
                                        </pre>
                                    </div>
                                )}

                                {llmRawOutput && (
                                    <div className="space-y-1">
                                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                            Raw output JSON
                                        </div>
                                        <pre className="max-h-64 overflow-auto rounded-md bg-muted p-2 text-[11px] leading-4">
                                            {JSON.stringify(llmRawOutput, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </CollapsibleContent>
                        </Collapsible>
                    </section>
                )}
            </CardContent>
        </Card>
    );
}
